import { Loader2, AlertCircle, ChevronLeft, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function Button({ variant = "primary", size, icon: Icon, loading, children, className = "", ...p }) {
  return (
    <button className={`btn btn-${variant} ${size === "sm" ? "btn-sm" : ""} ${className}`} disabled={loading || p.disabled} {...p}>
      {loading ? <Loader2 size={16} className="animate-spin" /> : Icon && <Icon size={17} />}
      {children}
    </button>
  );
}

export const Card = ({ className = "", children, ...p }) => (
  <div className={`card ${className}`} {...p}>{children}</div>
);

export function Field({ label, error, hint, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && <span className="label">{label}</span>}
      {children}
      {hint && <span className="caption">{hint}</span>}
      {error && <span className="caption" style={{ color: "var(--danger)" }}>{error}</span>}
    </label>
  );
}

export const Input = ({ className = "", ...p }) => <input className={`input ${className}`} {...p} />;

export const Chip = ({ active, children, ...p }) => (
  <button type="button" className={`chip ${active ? "chip-active" : ""}`} {...p}>{children}</button>
);

export const Badge = ({ tone = "brand", children, ...p }) => <span className={`badge badge-${tone}`} {...p}>{children}</span>;

export const Switch = ({ on, onChange, ...p }) => (
  <button type="button" role="switch" aria-checked={!!on} className={`switch ${on ? "on" : ""}`} onClick={() => onChange(!on)} {...p} />
);

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="seg" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" className={value === o.value ? "on" : ""} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ title, text, action, testid = "empty-state" }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-12 fade-in" data-testid={testid}>
      <img src="https://images.pexels.com/photos/7957726/pexels-photo-7957726.jpeg?auto=compress&cs=tinysrgb&w=400" alt="" className="w-28 h-28 object-cover rounded-full opacity-90" />
      <div className="h3">{title}</div>
      {text && <p className="muted max-w-sm text-sm">{text}</p>}
      {action}
    </div>
  );
}

export const Loading = () => (
  <div className="flex justify-center py-14" data-testid="loading-indicator">
    <Loader2 className="animate-spin" size={28} style={{ color: "var(--brand)" }} />
  </div>
);

export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12" data-testid="error-state">
      <AlertCircle size={30} style={{ color: "var(--danger)" }} />
      <p className="text-center">{message}</p>
      {onRetry && <Button variant="secondary" onClick={onRetry} data-testid="retry-button">Tentar novamente</Button>}
    </div>
  );
}

export function PageHeader({ title, subtitle, back, right, close }) {
  const nav = useNavigate();
  return (
    <div className="flex items-start gap-3 mb-6 fade-in">
      {(back || close) && (
        <button className="icon-btn mt-1 shrink-0" onClick={() => (typeof back === "string" ? nav(back) : nav(-1))} data-testid="header-back-button">
          {close ? <X size={18} /> : <ChevronLeft size={20} />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="h1 truncate">{title}</h1>
        {subtitle && <p className="caption mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export const Page = ({ children, narrow, className = "" }) => (
  <div className={`mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-12 ${narrow ? "max-w-2xl" : "max-w-6xl"} ${className}`}>{children}</div>
);

export function StickyFooter({ children }) {
  return (
    <div className="sticky bottom-20 lg:bottom-4 mt-6 z-10">
      <div className="card flex gap-3 justify-end shadow-lg" style={{ padding: 12 }}>{children}</div>
    </div>
  );
}
